import tw from 'tailwind-styled-components'
import {DateTime} from '@components/common/base/date-time'
import {DivWithHint} from '@components/common/hint/hint'
import {cn} from '@containers/html-utils'
import {Tail} from './tail'

/*
Min width for all parent containers wrapping an element with `whitespace: nowrap`
is set to 0 due to [default behaviour of flex](https://www.w3.org/TR/css-flexbox-1/#min-size-auto)
described in an example [here.](https://stackoverflow.com/questions/38223879/white-space-nowrap-breaks-flexbox-layout)
 */

export const TwChatMessageContainer = tw.div<{
  $my?: boolean
  $fullView: boolean
  $hideAvatar: boolean
  $animateShow?: boolean
  $editing?: boolean
}>`
  message-item-new
  isolate
  relative
  min-w-0
  py-4
  group
  flex
  gap-8
  items-end
  ${p =>
    cn(
      p.$my ? 'flex-row-reverse' : 'flex-row',
      p.$my && 'pl-64 pr-16',
      p.$fullView && !p.$my && (p.$hideAvatar ? 'pl-16 pr-32' : 'pl-8 pr-32'),
      !p.$fullView && !p.$my && (p.$hideAvatar ? 'pl-16 pr-32' : 'pl-48 pr-32'),
      p.$animateShow && 'opacity-0 animate-show-message',
      p.$editing &&
        'bg-[#b0d0e99a] bg-blend-multiply !animate-none !transition-none'
    )}
`

export const TwChatMessage = tw.div<{$my?: boolean}>`
  min-w-0
  max-w-472
  relative
  flex-auto
  p-12
  rounded-8
  ${p => cn(p.$my ? 'bg-personal-message' : 'bg-group-message')}
`

export const TwButtonsRow = tw.div`
  relative
  flex
  gap-8
  items-center
  min-w-0
  h-16
`

export const TwAdditionalInfo = tw.span`
  inline-flex
  items-center
  gap-4
  h-[11px]
`

export const TwSeenBy = tw(DivWithHint)`
  h-[11px]
  flex-none
  flex
  items-center
  justify-center
`

export const TwEditedAt = tw(DivWithHint)`
  flex-none
  text-secondary
  !text-11
  !leading-[11px]
  italic
`

export const TwDateTime = tw(DateTime)`
  min-w-max
  whitespace-nowrap
  !text-11
  !leading-[11px]
  text-secondary
`

export const TwButtons = tw.div<{$locked?: boolean}>`
  absolute
  right-0
  ml-auto
  flex-none
  invisible
  flex
  group-hover:relative
  group-hover:visible
  ${p => cn(p.$locked && cn('!relative', '!visible'))}
`

export const TwMessage = tw.div<{
  $larger?: boolean
  $heightRestricted?: boolean
}>`
  message-item-new__text
  message-text
  relative
  text-primary
  break-words
  ${p =>
    cn(
      !p.$heightRestricted && 'whitespace-pre-wrap',
      p.$larger && cn('text-32', 'leading-[32px]'),
      p.$larger &&
        p.$heightRestricted &&
        cn('max-h-32', 'whitespace-nowrap', 'overflow-hidden', 'text-ellipsis'),
      !p.$larger && 'plain-text-regular',
      !p.$larger &&
        p.$heightRestricted &&
        cn(
          'max-h-[17px]',
          'whitespace-nowrap',
          'overflow-hidden',
          'text-ellipsis'
        )
    )}
`

export const TwCitedMessage = tw.div<{$hoverable?: boolean}>`
  cursor-pointer
  pl-16
  border-solid
  border-l-4
  border-r-0
  border-y-0
  border-action-default
  ${p => cn(p.$hoverable && 'hover:bg-interactive-l100')}
`

export const TwTail = tw(Tail)`
  absolute
  -z-10
  bottom-0
  ${p =>
    p.my
      ? '-right-6 fill-personal-message text-personal-message'
      : '-left-6 fill-group-message text-group-message'}
`

// todo: investigate if it's still required
export const TwUiCnt = tw.div`
  !m-0
  ui-cnt
`

export const TwChatMessageBottom = tw.div`
  !m-0
  chat-message-bottom
`
